import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Switch } from '@sorare/core/src/components/search/Switch';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useDefaultAverageScore } from '@sorare/core/src/hooks/useDefaultAverageScore';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import { AveragePlayerScoreIcon } from './AveragePlayerScoreIcon/index.tsx';

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  width: var(--triple-unit);
  height: var(--double-unit);
`;

const averageScoreMessages = defineMessages({
  [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]: {
    id: 'playerDetails.performance.lastFifteenSO5AverageScore',
    defaultMessage: 'Last 15 avg. ',
  },
  [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]: {
    id: 'playerDetails.performance.lastFiveSO5AverageScore',
    defaultMessage: 'Last 5 avg.',
  },
  [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]: {
    id: 'playerDetails.performance.lastTenPlayedSO5AverageScore',
    defaultMessage: 'Last 10 played avg.',
  },
  [AveragePlayerScore.SEASON_AVERAGE_SCORE]: {
    id: 'playerDetails.performance.seasonAverageScore',
    defaultMessage: 'Season avg.',
  },
  [AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE]: {
    id: 'playerDetails.performance.lastFortySO5AverageScore',
    defaultMessage: 'Last 40 avg.',
  },
});

const useAvailableSortBySport = (): Record<
  Sport,
  TotalAveragePlayerScore[]
> => {
  const defaultAverageScore = useDefaultAverageScore();
  return {
    [Sport.FOOTBALL]: [
      AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
      defaultAverageScore,
      AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
    ],
    [Sport.NBA]: [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE],
    [Sport.BASEBALL]: [
      defaultAverageScore,
      AveragePlayerScore.SEASON_AVERAGE_SCORE,
    ],
  };
};

export const AverageScores = () => {
  const { statsManager, updatePlayerSearchViewAttribute } =
    useManagePlayerSearchView();
  const { sport } = useSportContext();

  const availableSortBySport = useAvailableSortBySport();
  const availableSort = availableSortBySport[sport];
  const averageScores =
    statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE];

  const onAverageScoresChange = (input: TotalAveragePlayerScore[]) =>
    updatePlayerSearchViewAttribute(
      PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE,
      input
    );

  const showAppearances =
    statsManager[PlayerSearchViewAttributes.SHOW_APPEARANCES];

  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage {...playerDetails.performance} />
      </LabelM>
      {availableSort.map(sort => (
        <Switch
          key={sort}
          checked={averageScores.includes(sort)}
          onChange={() =>
            onAverageScoresChange(
              averageScores.includes(sort)
                ? averageScores.filter(s => s !== sort)
                : [...averageScores, sort]
            )
          }
          label={
            <Horizontal>
              <AveragePlayerScoreIcon variant={sort} />
              <FormattedMessage {...averageScoreMessages[sort]} />
            </Horizontal>
          }
        />
      ))}
      <Switch
        checked={showAppearances}
        onChange={() =>
          updatePlayerSearchViewAttribute(
            PlayerSearchViewAttributes.SHOW_APPEARANCES,
            !showAppearances
          )
        }
        label={
          <Horizontal>
            <StyledFontAwesomeIcon icon={faPercent} />
            <FormattedMessage
              id="playerDetails.performance.showAppearances"
              defaultMessage="Show appearances"
            />
          </Horizontal>
        }
      />
    </Vertical>
  );
};
